import { FC, useContext, useState, useMemo } from 'react';
import { Select, Typography, Tooltip, Button, Divider, Modal, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formActions, formStore } from '../../../store';
import { formStateDescription, formVersionNumberPrefix } from '#types/System.Parameters.types';
import { TimeType } from '#types/System.types';
import SelectCurrentVersion from './SelectCurrentVersion';
import { PlusOutlined } from '@ant-design/icons';
import FlexCol from '../../atoms/FlexCol';
import Icon from '../../atoms/Icon';
import { UserContext } from '../../../providers/UserContext';
import { useFormWorkflowAction } from '../../../providers/artifacts/FormWorkflowActionContext';
import { checkRequiredFields } from '../../../use/form/checkRequiredFields';
import { createFormVersion } from '../../../services/artifactFunctions';

dayjs.extend(relativeTime);

const { Text } = Typography;

/**
 * FormVersionSelect
 *
 * The version dropdown, with the per-version actions in its footer: New
 * Version, Set as Current, Archive Version. The form number leads the
 * `FormControl` bar as `FormNumberLabel` and is no longer drawn here.
 *
 * A form may have zero versions, so both paths tolerate a missing current
 * version: New Version then calls `createFormVersion` without a source (the
 * callable builds it from the form type), and Archive Version — which needs a
 * version to act on — is disabled rather than hidden.
 *
 * Reads leaves off a narrow snapshot — never `useFormStore()`, whose spread of
 * `formVersion.data` re-renders on every keystroke in the form. The version
 * options are built by walking `formVersions.docIds` and reading only `state`,
 * `formVersionNumber` and `meta.lastModified` from `formVersions.docs.get(id)`:
 * that collection listener covers the open version too, so spreading an entry
 * would resubscribe this control to every field-value write.
 *
 * `meta.lastModified` is read only while the dropdown is open. It is the one
 * leaf here the server bumps on every save, and the closed control renders
 * `title` (number + state), which never carries it — so reading it
 * unconditionally re-rendered this control on every keystroke to refresh a
 * relative timestamp nobody could see.
 *
 * `form.formNumber` is gated the same way, on the archive modal being open: the
 * confirmation copy is the only place left that names it, so an ungated read
 * would subscribe the control to a leaf it never displays.
 *
 * New Version runs the same `checkRequiredFields` gate as the toolbar dropdown
 * and the right-click menu, but only when there is a source version: this is the
 * entry point whose gate has to tolerate a missing one, because the gate reads
 * the open version's fields and this control still renders on a form with no
 * versions, where the other surfaces return early. An unconditional gate would
 * report every required field in the layout missing and open
 * `FormRequiredFieldsModal` over a form with no version to fill. It is not the
 * only way to create that first version — `FormNoVersions` leads the same state
 * with its own "Create first version" button.
 *
 * In-flight state is the `isCreating` flag on `FormWorkflowActionContext`, not a
 * local one, so one create locks all four create surfaces at once — this
 * control, `FormWorkflowSelect`, `FormContextMenu` and `FormNoVersions` — and no
 * second `createFormVersion` can be fired from any of them. The footer's other
 * two actions, Set Current and Archive Version, are disabled for the same
 * window: they act on a version list the pending create is about to change
 * (SYD-2467).
 */
const FormVersionSelect: FC = () => {
  const navigate = useNavigate();
  const snap = useSnapshot(formStore.state);
  const formId = snap.formId;
  const versionId = snap.versionId;
  const currentVersionId = snap.form.data?.currentVersionId;
  const openVersionNumber = snap.formVersion.data?.formVersionNumber;
  const requirementsMet = !snap.form.loading && !snap.formVersion.loading && !snap.formType.loading;
  const selectedFormVersionMatchesId = !!snap.versionId && snap.formVersion.data !== null;

  const { userRights } = useContext(UserContext);
  const { isCreating, setIsCreating } = useFormWorkflowAction();
  const [messageApi, contextHolder] = message.useMessage();
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const formNumber = isArchiveModalOpen ? snap.form.data?.formNumber : undefined;

  const formatDate = (time: TimeType | undefined) => {
    if (time && 'toDate' in time) {
      const date = time.toDate();
      return {
        relative: dayjs(date).fromNow(),
        full: date.toLocaleString()
      };
    }
    return {
      relative: '',
      full: ''
    };
  };

  const handleVersionChange = (selectedVersionId: string) => {
    if (formId) {
      formActions.navigateToVersion(formId, selectedVersionId);
    }
  };

  const handleCreateVersion = async () => {
    if (!formId) return;
    if (versionId && !checkRequiredFields()) return;

    setIsCreating(true);
    try {
      const newVersionId = await createFormVersion(formId, versionId, 'draft');
      setIsDropdownOpen(false);
      formActions.navigateToVersion(formId, newVersionId);
    } catch (err) {
      messageApi.error(`Error creating new version: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsCreating(false);
    }
  };

  const hasArchivePermission = useMemo(() => {
    return !!(userRights.orgsAdmin || userRights.formsAdmin || userRights.formsArchive);
  }, [userRights]);

  const handleArchiveFormVersion = async () => {
    if (!formId || !versionId) return;

    const result = await formActions.archiveFormVersion(formId, versionId);
    if (result.success) {
      setIsArchiveModalOpen(false);
      navigate('/forms');
      setTimeout(() => {
        messageApi.success('Form version has been archived');
      }, 100);
    } else {
      const errorMsg = result.error || 'Failed to archive form version';
      messageApi.error(errorMsg);
      setIsArchiveModalOpen(false);
    }
  };

  const handleArchiveClick = () => {
    setIsArchiveModalOpen(true);
  };

  const currentVersionState = snap.formVersion.data?.state;
  const possibleStates = snap.formType.data?.possibleStates;
  const allowedNextStates = currentVersionState
    ? possibleStates?.[currentVersionState]?.allowedNextStates
    : undefined;
  const canCreateNewVersion = requirementsMet
    && !!formId
    && (!versionId || (allowedNextStates?.includes('draft') ?? false));

  const versionOptions = snap.formVersions.docIds.flatMap(id => {
    const version = snap.formVersions.docs.get(id)?.data;
    if (!version) return [];

    const versionNumber = version.formVersionNumber ? formVersionNumberPrefix + version.formVersionNumber.toString() + ' ' : '- ';
    const formState = formStateDescription[version.state]?.shortLabel;
    const isCurrentVersion = currentVersionId === id;
    const title = `${versionNumber} | ${formState}${isCurrentVersion ? '—current' : ''}`;

    return [{
      value: id,
      label: isDropdownOpen ? (
        <Tooltip title={formStateDescription[version.state]?.longDescription || ''}>
          <Text>
            {versionNumber}
          </Text>
          <Text>
            &nbsp;{formState}&nbsp;
          </Text>
          <Text type="secondary">
            {formatDate(version.meta?.lastModified).relative}
          </Text>
          {isCurrentVersion && (
            <Text type="success" style={{ marginLeft: 10 }}>
              current
            </Text>
          )}
        </Tooltip>
      ) : title,
      title
    }];
  });

  return (
    <>
      {contextHolder}
      <Modal
        title="Archive Form Version"
        open={isArchiveModalOpen}
        onOk={handleArchiveFormVersion}
        onCancel={() => setIsArchiveModalOpen(false)}
        okText="Archive"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to archive version {openVersionNumber || 'N/A'} of form "{formNumber}"?
        </p>
        <p>
          This will archive only this specific version. The version can be restored later from the archive.
        </p>
      </Modal>
      <Select
        aria-label="Form version"
        value={selectedFormVersionMatchesId ? versionId : undefined}
        loading={snap.formVersions.loading}
        optionLabelProp='title'
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        onChange={handleVersionChange}
        options={versionOptions}
        style={{ minWidth: 120 }}
        styles={{
          popup: {
            root: {
              width: 320
            }
          }
        }}
        popupRender={(menu) => (
          <>
            {menu}
            <FlexCol style={{ marginTop: 5 }}>
              <Divider style={{ margin: 0 }} />
              <Button
                type="text"
                aria-label="New version"
                icon={<PlusOutlined />}
                loading={isCreating}
                disabled={!canCreateNewVersion || isCreating}
                onClick={handleCreateVersion}
                size="small"
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-start',
                }}
              >
                New Version
              </Button>
              <Divider style={{ margin: 0 }} />
              <SelectCurrentVersion disabled={isCreating} />
              {hasArchivePermission && (
                <>
                  <Divider style={{ margin: 0 }} />
                  <Button
                    type="text"
                    aria-label="Archive version"
                    icon={<Icon.Archive />}
                    onClick={handleArchiveClick}
                    size="small"
                    danger
                    disabled={!selectedFormVersionMatchesId || isCreating}
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-start',
                    }}
                  >
                    Archive Version
                  </Button>
                </>
              )}
            </FlexCol>
          </>
        )}
      />
    </>
  );
};

export default FormVersionSelect; 